/**
 * Вкладка «Обзвон» в «Чатах» (разметка владельца 20.10.26): кнопка «Звонить» и карточка клиента,
 * у которого переписки в «Чатах» нет. Звонок — ссылкой `sip:+7…`: её перехватывает софтфон
 * сотрудника (MicroSIP), сам кабинет никуда не звонит. Клиент с перепиской открывается обычной
 * лентой чата (`Thread` в `pages/ChatsPage`) — всеми каналами сразу и с этой же кнопкой в шапке.
 * Сверху списка — «Взять» (окно «Выделить» расширения: те же очереди и правила) и «Отдать» (снять
 * с себя все лиды «Не обработан» и сделки обзвона), у владельца ещё «Мои / Все» — разметка 20.10.26.
 * Спека `docs/specs/2026-10-20-chat-dial-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Phone, Search, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { fmtPhone } from "@/components/chat/Contacts";
import { CreateDeal, DEAL_CARD_STALE, DealBrief, DealHistory, LeadStatusSwitch } from "@/components/chat/DealPanel";
import { Take } from "@/ext/Take";
import { apiFetch } from "@/lib/api";
import { loadCall } from "@/lib/calls";
import { type DialCall, type DialItem, type DialScope, regionText } from "@/lib/clientChats";
import { btn } from "@/lib/panelBtn";
import { loadDealCard } from "@/lib/dealCard";
import { loadLeadCard } from "@/lib/leadCard";
import { loadSearch, type SearchResult } from "@/lib/search";

/** «Звонить»: `sip:` с номером (по `tel:` у Ненашевой ничего не открывалось, 21.10.26) — софтфон перехватывает ссылку и набирает. */
export function CallButton({ phone, compact = false }: { phone: string; compact?: boolean }) {
  return (
    <a
      href={`sip:+${phone}`}
      title={`Позвонить ${fmtPhone(phone)} — звонок наберёт софтфон (MicroSIP)`}
      className={`flex flex-none items-center justify-center gap-1.5 rounded-full bg-[#2e7d32] font-medium text-white hover:bg-[#256b29] ${
        compact ? "size-9" : "h-9 px-3.5 text-[14px] max-md:w-9 max-md:px-0"
      }`}
      data-annot="звонить"
    >
      <Phone size={16} aria-hidden />
      {/* На телефоне — только трубка: место в шапке нужно имени клиента. */}
      {!compact && <span className="max-md:hidden">Звонить</span>}
    </a>
  );
}

/** Откуда лид словами: заголовок Bitrix без номера в начале — «Входящий звонок», «Записаться»; у звонка — линия. */
export function dialSource(item: DialItem): string {
  if (item.call) return item.title ? `линия «${item.title}»` : "";
  return item.title.replace(/^[\d\s()+-]+-\s*/, "").trim();
}

/** «2:15» — длительность звонка в минутах и секундах. */
export const mmss = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

/** Звонок вкладки «Звонки» словами: «входящий · разговор 2:15», «исходящий · не дозвонились». */
export function callText(call: DialCall): string {
  const dir = call.direction === "in" ? "входящий" : "исходящий";
  const result = {
    talk: `разговор ${mmss(call.duration)}`,
    none: `соединились, разговора не было · ${mmss(call.duration)}`,
    missed: "пропущен",
    noanswer: "не дозвонились",
  }[call.result];
  return `${dir} · ${result}`;
}

/** Имя клиента; имя чата WhatsApp — это номер, его именем не считаем. */
export const dialPerson = (item: DialItem) => (/^[\d\s()+-]*$/.test(item.name) ? "" : item.name);

/** Имя клиента, без него — номер. */
export const dialName = (item: DialItem) =>
  dialPerson(item) ||
  (item.phone
    ? fmtPhone(item.phone)
    : item.kind === "call"
      ? "Номер скрыт"
      : item.kind === "lead"
        ? `Лид ${item.id}`
        : `Сделка ${item.id}`);

/**
 * Объект Bitrix строки: лид или сделка обзвона — сами; у пропущенного звонка и строки «Звонков» —
 * сделка или лид номера из своей копии (может не быть вовсе).
 */
export function dialObject(item: DialItem): { kind: "lead" | "deal"; id: number } | null {
  if (item.kind === "lead" || item.kind === "deal") return { kind: item.kind, id: item.id };
  if (item.deal) return { kind: "deal", id: item.deal.id };
  if (item.lead) return { kind: "lead", id: item.lead.id };
  return null;
}

const hhmm = (iso: string) => iso.slice(11, 16);
const ddmm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
/** «пропущен 10:42» сегодня, «пропущен 19.10 09:31» раньше. */
const missedWhen = (iso: string) => {
  const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Moscow" });
  return iso.startsWith(today) ? hhmm(iso) : `${ddmm(iso)} ${hhmm(iso)}`;
};

/** Красная пометка «ждёт перезвона»: пропущенный входящий, разговора с номером после него не было. */
export function missedText(item: DialItem, long = false): string {
  const m = item.missed;
  if (!m) return "";
  const parts = [`ждёт перезвона · пропущен ${missedWhen(m.at)}`];
  if (long) {
    if (m.count > 1) parts.push(`пропусков ${m.count} с ${missedWhen(m.from)}`);
    parts.push(`ждал ${m.wait} с`);
    if (m.line) parts.push(`линия «${m.line}»`);
    parts.push(m.attempts ? `перезванивали ${m.attempts} из 3` : "ещё не перезванивали");
    if (m.last_try) parts.push(`последняя попытка ${missedWhen(m.last_try)}`);
  }
  return parts.join(" · ");
}

/** Последняя попытка перезвона на пропущенный — «перезвон 09:26 · 1 из 3»; без попыток — пусто. */
export function missedTryText(item: DialItem): string {
  const m = item.missed;
  if (!m?.last_try) return "";
  return `перезвон ${missedWhen(m.last_try)} · ${m.attempts} из 3`;
}

/** Что за объект и сколько уже звонили — подпись под именем. */
export function dialWhat(item: DialItem): string {
  const o = dialObject(item);
  const obj = !o ? "в Bitrix не найден" : o.kind === "lead" ? `Лид ${o.id}` : `Сделка ${o.id}`;
  const state = item.deal?.stage ?? item.lead?.status ?? "";
  const calls = item.call
    ? callText(item.call)
    : item.found
      ? ""
      : item.calls_today
        ? `сегодня звонили ${item.calls_today}`
        : "сегодня не звонили";
  return [obj, state, calls].filter(Boolean).join(" · ");
}

/** Номер в строке поиска «Чатов» — 11 цифр с 7 («8 926 …», «926 …» тоже), иначе пусто. */
export function searchPhone(q: string): string {
  if (!/^[\d\s().+-]+$/.test(q)) return "";
  const d = q.replace(/\D/g, "");
  if (d.length === 10 && d.startsWith("9")) return `7${d}`;
  if (d.length === 11 && /^[78]/.test(d)) return `7${d.slice(1)}`;
  return "";
}

/**
 * Сделки и лиды номера из живого поиска — строками «Обзвона»: чата с номером нет, а карточку
 * открыть нужно (например, конвертировать лид в сделку). Главный объект и действующие — первыми.
 * Ответ поиска про другой номер — ничего: строка несла бы этот номер со сделкой чужого клиента.
 */
export function foundItems(s: SearchResult, phone: string): DialItem[] {
  if (searchPhone(s.query) !== searchPhone(phone)) return [];
  const person = s.contacts.map((c) => c.name).find((n) => n && !/^[\d\s()+-]*$/.test(n)) ?? "";
  const base = { name: person, phone, region: null, chat: null, chats: [], calls_today: 0, last_out: null, last_call: null, owner: null, missed: null, found: true as const };
  const main = (kind: "deal" | "lead", id: number) => s.main?.entity === kind && s.main.id === id;
  const rank = (kind: "deal" | "lead", id: number, active: boolean) => (main(kind, id) ? 0 : active ? 1 : 2);
  const deals = s.deals.map((d) => ({
    rank: rank("deal", d.id, d.active),
    item: {
      ...base,
      key: `found-deal-${d.id}`,
      kind: "deal" as const,
      id: d.id,
      title: d.title ?? "",
      created: d.created,
      deal: { id: d.id, stage: d.stage_name ?? d.stage ?? "", stage_code: d.stage ?? "", kind: "other" as const, date: null, rent_start: null, rent_end: null },
      lead: null,
    },
  }));
  const leads = s.leads.map((l) => ({
    rank: rank("lead", l.id, l.active),
    item: {
      ...base,
      key: `found-lead-${l.id}`,
      kind: "lead" as const,
      id: l.id,
      title: l.title ?? "",
      created: l.created,
      deal: null,
      lead: { id: l.id, status: l.status_name, status_code: l.status, active: l.active, date: null },
    },
  }));
  return [...deals, ...leads].sort((a, b) => a.rank - b.rank).map((x) => x.item);
}

/**
 * Номер, по которому в Bitrix ничего нет и чата нет (всплывашка «Входящий», пустой поиск): карточка
 * номера — «Звонить», всё по номеру и форма «Создать сделку».
 */
export function phoneItem(phone: string): DialItem {
  return {
    key: `phone-${phone}`,
    kind: "call",
    id: 0,
    name: "",
    title: "",
    phone,
    region: null,
    created: null,
    deal: null,
    lead: null,
    chat: null,
    chats: [],
    calls_today: 0,
    last_out: null,
    last_call: null,
    owner: null,
    missed: null,
    found: true,
  };
}

/**
 * Под пустым поиском «Чатов» по номеру: сделки и лиды номера в Bitrix. Клик — карточка, как у
 * «Обзвона» (`DialCard`): «Звонить», статус лида и «Сделка» из лида, вся история.
 */
const withDial = (search: string, key: string) => {
  const p = new URLSearchParams(search);
  p.set("dial", key);
  return p.toString();
};

export function FoundByPhone({
  phone,
  active,
  onPick,
  hasChats = false,
}: {
  phone: string;
  active: string | null;
  onPick: (item: DialItem) => void;
  hasChats?: boolean;
}) {
  const { search } = useLocation();
  const found = useQuery({ queryKey: ["quick-jump", phone], queryFn: () => loadSearch(phone), staleTime: 60_000 });
  const items = found.data ? foundItems(found.data, phone) : [];
  // Под пустым поиском — с оговоркой «чата нет», в окне у найденных чатов — без неё.
  const say = (s: string) => (hasChats ? s[0]!.toUpperCase() + s.slice(1) : `Чата с ${fmtPhone(phone)} нет — ${s}`);
  return (
    <div className="flex flex-col gap-1" data-annot="найдено по номеру">
      <p className="px-2.5 pb-1 pt-2 text-[13px] text-[var(--tg-sec)]">
        {found.isPending
          ? say("ищу лиды и сделки номера…")
          : found.isError
            ? say(`поиск в Bitrix не удался: ${found.error.message}`)
            : items.length
              ? say("в Bitrix по номеру:")
              : say("в Bitrix по номеру ни лидов, ни сделок.")}
      </p>
      {found.data && !items.length && (
        <Link
          to={{ pathname: "/chats", search: withDial(search, `phone-${phone}`) }}
          onClick={() => onPick(phoneItem(phone))}
          className={`flex items-center gap-3 rounded-[16px] px-2.5 py-2 ${active === `phone-${phone}` ? "bg-[var(--tg-primary)] text-white" : "hover:bg-[var(--tg-hover)]"}`}
          data-annot="найдено по номеру: создать сделку"
        >
          <span className="grid size-[42px] flex-none place-items-center rounded-full bg-[var(--tg-sec)] text-white">
            <Phone size={20} />
          </span>
          <span className="min-w-0 flex-1 truncate font-medium">{fmtPhone(phone)} — создать сделку</span>
        </Link>
      )}
      {items.map((it) => {
        const on = it.key === active;
        const live = it.deal ? found.data?.deals.find((d) => d.id === it.id)?.active : it.lead?.active;
        return (
          <Link
            key={it.key}
            to={{ pathname: "/chats", search: withDial(search, it.key) }}
            onClick={() => onPick(it)}
            className={`flex items-center gap-3 rounded-[16px] px-2.5 py-2 ${on ? "bg-[var(--tg-primary)] text-white" : "hover:bg-[var(--tg-hover)]"}`}
            data-annot="найдено по номеру: строка"
          >
            <span className={`grid size-[42px] flex-none place-items-center rounded-full text-white ${live ? "bg-[#2e7d32]" : "bg-[var(--tg-sec)]"}`}>
              <Phone size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">
                {it.kind === "lead" ? `Лид ${it.id}` : `Сделка ${it.id}`}
                {dialPerson(it) && ` · ${dialPerson(it)}`}
              </div>
              <div className={`truncate text-[13px] ${on ? "text-white/80" : "text-[var(--tg-sec)]"}`}>
                {line(it.deal?.stage ?? it.lead?.status, ddmmyy(it.created), dialSource(it))}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

/**
 * Номер в поиске и чаты с ним нашлись: кнопка над списком открывает окном те же лиды и сделки номера
 * из живого поиска, что `FoundByPhone` под пустым поиском (сделка без лида тоже — поиск идёт по
 * контакту). Клик по строке — карточка «Обзвона», окно закрывается.
 */
export function FoundByPhoneButton({ phone, active, onPick }: { phone: string; active: string | null; onPick: (item: DialItem) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-1 flex w-full items-center gap-2 rounded-[12px] bg-[var(--tg-primary)]/10 px-3 py-2 text-left text-[13px] text-[var(--tg-primary)] hover:bg-[var(--tg-primary)]/15"
        data-annot="лиды и сделки номера: кнопка"
      >
        <Search size={16} className="flex-none" aria-hidden />
        <span className="min-w-0 flex-1 truncate font-medium">Лиды и сделки {fmtPhone(phone)} в Bitrix</span>
      </button>
      {open && (
        <Modal title={`Лиды и сделки ${fmtPhone(phone)}`} annot="лиды и сделки номера: окно" onClose={() => setOpen(false)}>
          <FoundByPhone
            phone={phone}
            active={active}
            hasChats
            onPick={(it) => {
              onPick(it);
              setOpen(false);
            }}
          />
          <Link to={`/search?q=${phone}`} className="mt-2 block px-2.5 text-[13px] text-[var(--tg-primary)] hover:underline">
            Всё по номеру в «Поиске»: контакты, клиенты салона, договоры →
          </Link>
        </Modal>
      )}
    </>
  );
}

/** История объекта: лента карточки лида (у лида со сделкой — уже сделки) или сделки. */
function History({ obj }: { obj: { kind: "lead" | "deal"; id: number } }) {
  const lead = useQuery({
    queryKey: ["lead-card", obj.id],
    queryFn: () => loadLeadCard(obj.id),
    enabled: obj.kind === "lead",
    staleTime: DEAL_CARD_STALE,
  });
  const dealId = obj.kind === "deal" ? obj.id : (lead.data?.redirect ?? null);
  const deal = useQuery({
    queryKey: ["deal-card", dealId],
    queryFn: () => loadDealCard(dealId ?? 0),
    enabled: dealId !== null,
    staleTime: DEAL_CARD_STALE,
  });
  const q = dealId !== null ? deal : lead;
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Собираю историю…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">История не загрузилась: {q.error.message}</div>;
  const data = dealId !== null ? deal.data : lead.data;
  return (
    <DealHistory
      title={dealId !== null ? "История сделки" : "История лида"}
      events={data?.events ?? []}
      errors={data?.errors ?? []}
    />
  );
}

const ddmmyy = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(2, 4)}` : "");
const line = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(" · ");

/**
 * Всё, что есть по номеру, — живой поиск `GET /api/search` (как «Поиск»): сделки и лиды со стадией,
 * клиенты салона и договоры CRM. Строка обзвона знает только свою копию, а у давнего клиента в ней
 * бывает один контакт без сделок (21.10.26: «в Bitrix не найден» при сделках «Сдали» и «Бронь Отказ»).
 * Закрытые — серым, главный объект — первым.
 */
function NumberObjects({ s }: { s: SearchResult }) {
  const main = (kind: "deal" | "lead", id: number) => s.main?.entity === kind && s.main.id === id;
  const deals = [...s.deals].sort((a, b) => Number(main("deal", b.id)) - Number(main("deal", a.id)));
  const empty = !s.deals.length && !s.leads.length && !s.customers.length && !s.contracts.length;
  const row = (active: boolean) => (active ? "" : "text-[var(--tg-sec)]");
  const link = "text-[var(--tg-primary)] hover:underline";
  return (
    <div className="flex flex-col gap-1 text-[13px]" data-annot="всё по номеру">
      <div className="mb-1 font-medium">Всё по номеру в Bitrix и CRM</div>
      {deals.map((d) => (
        <div key={`d${d.id}`} className={row(d.active)}>
          <Link to={`/deals/${d.id}`} className={link}>
            Сделка {d.id}
          </Link>{" "}
          · {line(d.category, d.stage_name ?? d.stage, ddmmyy(d.created), d.assigned)}
          {main("deal", d.id) && " · главная"}
        </div>
      ))}
      {s.leads.map((l) => (
        <div key={`l${l.id}`} className={row(l.active)}>
          <Link to={`/leads/${l.id}`} className={link}>
            Лид {l.id}
          </Link>{" "}
          · {line(l.status_name, ddmmyy(l.created), l.assigned)}
        </div>
      ))}
      {s.customers.map((c) => (
        <div key={`c${c.id}`} className="text-[var(--tg-sec)]">
          {line(`Клиент салона ${c.id}`, c.name, c.status, ddmmyy(c.date_start), c.deal_ids.length ? `сделка ${c.deal_ids.join(", ")}` : "")}
        </div>
      ))}
      {s.contracts.map((c) => (
        <div key={`k${c.id}`} className="text-[var(--tg-sec)]">
          {line(`Договор ${c.id}`, c.status, c.deal_id ? `сделка ${c.deal_id}` : "")}
        </div>
      ))}
      {empty && <div className="text-[var(--tg-sec)]">Номер ни к чему не привязан — сделку можно создать ниже.</div>}
      {s.errors.length > 0 && <div className="text-[#e53935]">{s.errors.join(" · ")}</div>}
    </div>
  );
}

/** Клиент обзвона без переписки: кто, «Звонить», всё по номеру, статус лида или блок сделки и вся история. */
export function DialCard({ item }: { item: DialItem }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [creating, setCreating] = useState(false);
  const back = () => {
    const p = new URLSearchParams(search);
    p.delete("dial");
    void navigate({ pathname: "/chats", search: p.toString() });
  };
  const column = "mx-auto w-full max-w-[728px] px-2 md:px-4";
  // Ключ — как у «Поиска» в шапке и карточки номера: один и тот же номер не ищем дважды.
  const found = useQuery({
    queryKey: ["quick-jump", item.phone ?? ""],
    queryFn: () => loadSearch(item.phone ?? ""),
    enabled: !!item.phone,
    staleTime: 60_000,
  });
  // Своя копия сделку номера ещё не знает (фон её подтянет) — объект карточки берём из живого поиска.
  const obj = dialObject(item) ?? (found.data?.main ? { kind: found.data.main.entity, id: found.data.main.id } : null);
  const card = "rounded-[18px] bg-[var(--tg-surface)] shadow-[0_1px_8px_1px_rgba(0,0,0,.12)]";
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-annot="карточка обзвона">
      <div className={`${column} flex-none`}>
        <header className="flex h-14 items-center gap-2 bg-[var(--tg-surface)] px-2 shadow-[0_1px_5px_-1px_rgba(0,0,0,.21)] max-md:-mx-2 md:h-12 md:rounded-[24px]">
          <button
            type="button"
            onClick={back}
            title="К списку"
            className="grid size-10 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)] md:hidden"
          >
            <ArrowLeft size={22} />
          </button>
          <div className="min-w-0 flex-1 pl-2 leading-tight">
            <div className="truncate font-medium">{dialName(item)}</div>
            <div className="truncate text-[14px] text-[var(--tg-sec)]">
              {[
                dialPerson(item) ? (item.phone ? fmtPhone(item.phone) : "номера нет") : "",
                item.region ? regionText(item.region) : "",
                dialSource(item),
                dialWhat(item),
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
          {item.phone && <CallButton phone={item.phone} />}
        </header>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className={`${column} flex flex-col gap-3 py-3`}>
          {item.missed && (
            <p className={`${card} px-4 py-3 text-[14px] font-medium text-[#e53935]`} data-annot="ждёт перезвона">
              {missedText(item, true)}
            </p>
          )}
          {item.call && item.call.result !== "missed" && item.call.result !== "noanswer" && (
            <div className={`${card} px-4 py-3`}>
              <CallRecord call={item.call} />
            </div>
          )}
          <p className={`${card} px-4 py-3 text-[14px] text-[var(--tg-sec)]`}>
            {/* Найденное поиском по номеру открывают и при чатах номера (кнопка «Лиды и сделки») — про переписку не говорим. */}
            {[
              item.found ? "" : "Переписки с клиентом в «Чатах» нет.",
              obj
                ? `Ниже — всё по номеру и история ${obj.kind === "lead" ? "лида" : "сделки"}: звонки с записью и расшифровкой, комментарии, смены статуса.`
                : "",
            ]
              .filter(Boolean)
              .join(" ") || "По номеру в Bitrix ничего нет."}
          </p>
          {item.phone && (
            <div className={`${card} px-4 py-3`}>
              {found.isPending ? (
                <div className="text-[13px] text-[var(--tg-sec)]">Ищу номер в Bitrix и CRM…</div>
              ) : found.isError ? (
                <div className="text-[13px] text-[#e53935]">Поиск по номеру не удался: {found.error.message}</div>
              ) : (
                <NumberObjects s={found.data} />
              )}
            </div>
          )}
          {/* В Bitrix по номеру ничего — сделку сразу здесь (просьба владельца 21.10.26), а не «создайте в Bitrix». */}
          {!obj && item.phone && found.data && !found.data.deals.length && !found.data.leads.length && (
            <CreateDeal
              chatId={null}
              fromCall={item.call?.id ?? 0}
              phone={item.phone}
              name={item.name}
              lead={null}
              title="В Bitrix по номеру ничего нет — создать сделку"
            />
          )}
          {obj && (
            <div className={`${card} p-3 text-[13px]`}>
              {obj.kind === "lead" ? (
                <>
                  <div className="mb-2 font-medium">Статус лида после звонка</div>
                  <LeadStatusSwitch
                    leadId={obj.id}
                    code={item.lead?.status_code ?? null}
                    chatId={null}
                    onDeal={() => setCreating((was) => !was)}
                    dealOpen={creating}
                  />
                  {creating && item.phone && (
                    <div className="mt-3">
                      <CreateDeal
                        chatId={null}
                        fromLead={obj.id}
                        phone={item.phone}
                        name={item.name}
                        lead={obj.id}
                        title="Сделка из лида"
                        onCancel={() => setCreating(false)}
                        onDone={() => setCreating(false)}
                      />
                    </div>
                  )}
                </>
              ) : (
                <DealBrief dealId={obj.id} chatId={null} quickComment />
              )}
            </div>
          )}
          {obj && (
            <div className={`${card} p-3`}>
              <History obj={obj} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Запись и текст звонка строки «Звонков» — из своей копии (`GET /api/calls/{id}`, запись —
 * `/api/calls/{id}/record`). Расшифровку делает фон раз в 10 минут: пока её нет, переспрашиваем.
 */
function CallRecord({ call }: { call: DialCall }) {
  const q = useQuery({
    queryKey: ["call", call.id],
    queryFn: () => loadCall(call.id),
    refetchInterval: (query) => (query.state.data?.transcript ? false : 60_000),
  });
  const c = q.data;
  return (
    <div className="flex flex-col gap-2 text-[13px]" data-annot="запись и текст звонка">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">Запись и текст звонка</span>
        <Link to={`/calls/${call.id}`} className="text-[12px] text-[var(--tg-sec)] hover:underline">
          карточка звонка
        </Link>
      </div>
      {q.isPending && <div className="text-[var(--tg-sec)]">Загрузка…</div>}
      {q.isError && <div className="text-[#e53935]">Звонок не загрузился: {q.error.message}</div>}
      {c && (c.record || call.record) && (
        <audio controls preload="none" src={`/api/calls/${call.id}/record`} className="h-9 w-full" />
      )}
      {c && !c.record && !call.record && <div className="text-[var(--tg-sec)]">Записи нет.</div>}
      {c?.transcript ? (
        <div className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap rounded-[12px] bg-[var(--tg-input)] px-3 py-2 leading-[1.5]">
          {c.transcript}
        </div>
      ) : (
        c && <div className="text-[var(--tg-sec)]">Расшифровки пока нет — фон делает её в течение 10–20 минут после звонка.</div>
      )}
    </div>
  );
}

/** Окно поверх «Чатов»: шапка с крестиком, Esc и клик мимо закрывают. */
function Modal({ title, annot, onClose, children }: { title: string; annot: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3" onClick={onClose}>
      <div
        className="flex max-h-full w-full max-w-[420px] flex-col overflow-hidden rounded-[16px] bg-bg text-[14px] text-fg"
        onClick={(e) => e.stopPropagation()}
        data-annot={annot}
      >
        <div className="flex flex-none items-center gap-2 border-b border-border px-4 py-2.5">
          <div className="min-w-0 flex-1 text-[15px] font-medium">{title}</div>
          <button type="button" onClick={onClose} title="Закрыть" className="grid size-8 place-items-center rounded-full hover:bg-bg-subtle">
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>
      </div>
    </div>
  );
}

/** Сводка «Выделить» (`GET /api/ext/take`): сколько на мне сейчас — для окна «Отдать». */
type Held = { leads: { held: number }; deals: { held: number } };
type Returned = {
  leads: { requested: number; changed: number; errors: { error: string }[] };
  deals: { requested: number; changed: number; errors: { error: string }[] };
};

const plural = (n: number, one: string, few: string, many: string) =>
  n % 10 === 1 && n % 100 !== 11 ? one : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? few : many;
const leadsN = (n: number) => `${n} ${plural(n, "лид", "лида", "лидов")}`;
const dealsN = (n: number) => `${n} ${plural(n, "сделку", "сделки", "сделок")}`;

/** «Отдать»: снять с себя всё взятое — подтверждение в самом окне. **Пишет в боевой Bitrix.** */
function GiveBack({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const held = useQuery({ queryKey: ["ext-take"], queryFn: () => apiFetch<Held>("/api/ext/take") });
  const give = useMutation({
    mutationFn: () => apiFetch<Returned>("/api/ext/take/return", { method: "POST" }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["chat-dial"] });
      void qc.invalidateQueries({ queryKey: ["ext-take"] });
    },
  });
  const r = give.data;
  const errors = r ? [...r.leads.errors, ...r.deals.errors] : [];
  return (
    <Modal title="Отдать взятое" annot="окно отдать" onClose={onClose}>
      <div className="flex flex-col gap-3 text-[13px]">
        {held.isPending && <p className="text-fg-muted">Считаю, что на вас…</p>}
        {held.isError && <p className="text-err">Не удалось посчитать: {held.error.message}</p>}
        {held.data && !r && (
          <p className="leading-snug">
            Сейчас на вас {leadsN(held.data.leads.held)} «Не обработан» и {dealsN(held.data.deals.held)} обзвона. «Отдать» снимет их
            с вас в Bitrix: лиды вернутся в нераспределённые, сделки — в общую очередь обзвона.
          </p>
        )}
        {r && (
          <p role="status" className={`rounded-[var(--radius-ui)] px-2 py-1.5 leading-snug ${errors.length ? "bg-warn-bg text-fg" : "bg-ok-bg text-ok"}`}>
            Снято: {leadsN(r.leads.changed)} и {dealsN(r.deals.changed)}
            {errors.length > 0 && ` · ошибок Bitrix: ${errors.length} (${errors[0]?.error ?? ""})`}
          </p>
        )}
        {give.isError && <p role="alert" className="rounded-[var(--radius-ui)] bg-err-bg px-2 py-1.5 text-err">{give.error.message}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn.quiet} onClick={onClose}>
            {r ? "Закрыть" : "Отмена"}
          </button>
          {!r && (
            <button type="button" className={btn.primary} disabled={!held.data || give.isPending} onClick={() => give.mutate()}>
              {give.isPending ? "Снимаю…" : "Отдать всё"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * Над списком «Обзвона»: «Мои / Все» (только владельцу — сервер прислал `all_allowed`), «Взять» —
 * окно «Выделить» расширения, «Отдать» — снять с себя взятое.
 */
export function DialTools({
  scope,
  allAllowed,
  onScope,
}: {
  scope: DialScope;
  allAllowed: boolean;
  onScope: (scope: DialScope) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<"take" | "give" | null>(null);
  const pill = (on: boolean) =>
    `flex h-8 items-center rounded-full px-3 text-[13px] font-medium ${on ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-sec)] hover:text-[var(--tg-text)]"}`;
  return (
    <div className="mx-3 mb-2 flex flex-none items-center gap-2" data-annot="кнопки обзвона">
      {allAllowed && (
        <div className="flex rounded-full bg-[var(--tg-input)] p-0.5" title="Только у владельца: все лиды «Не обработан» и сделки обзвона — на ком бы ни были">
          <button type="button" className={pill(scope === "mine")} onClick={() => onScope("mine")}>
            Мои
          </button>
          <button type="button" className={pill(scope === "all")} onClick={() => onScope("all")}>
            Все
          </button>
        </div>
      )}
      <div className="flex-1" />
      <button
        type="button"
        className="flex h-8 items-center rounded-full bg-[var(--tg-primary)] px-3.5 text-[13px] font-medium text-white hover:opacity-90"
        title="Взять себе лиды и сделки — по 15 за раз, правила как у «Распределения»"
        onClick={() => setOpen("take")}
        data-annot="взять"
      >
        Взять
      </button>
      <button
        type="button"
        className="flex h-8 items-center rounded-full border border-[var(--tg-sec)] px-3.5 text-[13px] font-medium text-[var(--tg-sec)] hover:text-[var(--tg-text)]"
        title="Снять с себя все лиды «Не обработан» и сделки обзвона"
        onClick={() => setOpen("give")}
        data-annot="отдать"
      >
        Отдать
      </button>
      {open === "take" && (
        <Modal title="Взять себе" annot="окно взять" onClose={() => setOpen(null)}>
          <Take active onTaken={() => void qc.invalidateQueries({ queryKey: ["chat-dial"] })} />
        </Modal>
      )}
      {open === "give" && <GiveBack onClose={() => setOpen(null)} />}
    </div>
  );
}
